import type { ViewFieldsFragment } from '@salescore/client-api'
import { selectAllRecordThreadStates, useAppSelector } from '@salescore/client-redux'
import type {
  CompiledFormViewConfig,
  CompiledKpiPivotViewConfig,
  CompiledKpiViewConfig,
  CompiledSheetViewConfig,
  EltChangeResult,
  ViewQueryList,
  ViewQueryRecordNodeWithId,
} from '@salescore/core'
import { wrapKeyboardEvent } from '@salescore/frontend-common'
import { message } from 'antd'
import { t } from 'i18next'
import { type Dispatch, type KeyboardEvent, type SetStateAction, useCallback } from 'react'

import { isCheckboxColumn, type RSheetColumn, type RSheetRecordNode } from '../../../domain/column'
import { useCursorMutation, useGetCursorSnapshot } from '../../../recoil/cursor'
import { useRecordsImpl } from '../../../recoil/records'
import { useAddEmptyRecordMutation } from '../../../recoil/records/mutations/addEmptyRecordMutation'
import { useRecordsAndChangesMutation } from '../../../recoil/records/mutations/recordsAndChangesMutation'
import type { SalescoreObjectAbility, ViewAbility } from '../../../recoil/view/atoms'
import { isCellWriteProhibited } from '../../util/cellWriteProhibition'
import { getCellRelated } from '../../util/getCellValue'
import { usePaste } from '../hooks/usePaste'

export const useAsyncHandleKeyDown = (
  viewId: string,
  columns: Array<RSheetColumn<RSheetRecordNode>>,
  setRecordSaveErrors: Dispatch<SetStateAction<EltChangeResult[]>>,
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
  view: ViewFieldsFragment,
  ability: ViewAbility & SalescoreObjectAbility,
  query: ViewQueryList,
  // eslint-disable-next-line @typescript-eslint/max-params
): ((event: React.KeyboardEvent<HTMLDivElement>) => Promise<void>) => {
  const getCursorSnapshot = useGetCursorSnapshot()
  const recordsAndChangesMutation = useRecordsAndChangesMutation(compiledConfig, view, query)
  const paste = usePaste(columns, ability, query)
  const { addRootRecord, addChildRecord } = useAddEmptyRecordMutation(query, ability)
  const rows = useRecordsImpl()
  const { moveToNextInnerRow, moveToRowIndex } = useCursorMutation(ability)
  const openViewRecordThreads = useAppSelector((state) => selectAllRecordThreadStates(state, { viewId }))

  return useCallback(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    async (event: KeyboardEvent<HTMLDivElement>): Promise<void> => {
      const cursor = getCursorSnapshot()
      const hasOpenCommentPopups = Object.keys(openViewRecordThreads).length > 0
      if (cursor?.editing?.isEditing === true || hasOpenCommentPopups) {
        return
      }

      const wrappedEvent = wrapKeyboardEvent(event)

      /**
       * 空セルへのキーボード操作でレコードを作成する。真偽値はその場でチェックを付け (作成 + true)、
       * それ以外は通常の新規入力としてレコードを作成し編集モードへ繋ぐ。作成と値設定は1回の保存にまとめる (分けると作成直後の行に書き込めないため)
       * @returns 空セルに作成処理 (addChildRecord) を実行した = このキー入力をこのハンドラで処理した場合は true、
       *          作成対象外 (カーソル無 / 子ノードでない / 既にレコードあり / 書き込み禁止) でスキップした場合は false。呼出側 (Space) が preventDefault の要否に使う
       */
      const createEmptyChildRecord = async (
        column: RSheetColumn<RSheetRecordNode>,
        row: ViewQueryRecordNodeWithId,
      ): Promise<boolean> => {
        if (cursor === undefined || !(column.nodeType === 'children' || column.nodeType === 'child')) {
          return false
        }
        const { recordNode } = getCellRelated(column, row, cursor.main.innerRowIndex)
        if (recordNode !== undefined) {
          return false
        }
        // 書き込み禁止のセルではキーボード操作でレコードを作らない (checkbox の作成 + true も、テキスト等の値なし作成も共通)
        if (isCellWriteProhibited(column, row, query)) {
          // checkbox/boolean は編集モードが無く clickCurrentCursorMutation にも到達しないため、条件付き disable の書き込み禁止通知がどこからも出ない。
          // ここで補うのは条件付き disable のみ。列 readonly は同期ハンドラ (useHandleKeyDown / clickCurrentCursorMutation) が既に通知するので二重通知回避で除外する。
          if (isCheckboxColumn(column) && column.readonly !== true) {
            void message.warning(t(`このセルは書き込み禁止です`))
          }
          return false
        }
        const additionalFieldValues =
          isCheckboxColumn(column) && column.field !== undefined ? [{ field: column.field, value: true }] : []
        await addChildRecord({
          rowIndex: cursor.main.rowIndex,
          innerRowIndex: cursor.main.innerRowIndex,
          node: column.node,
          additionalFieldValues,
        })
        return true
      }

      // Space 用: 現在カーソルの空 checkbox/boolean 子セルにのみ作成 + true を行う (それ以外は同期側が担当するため何もしない)
      const createEmptyCheckboxChildRecordAtCursor = async (): Promise<boolean> => {
        if (cursor === undefined) {
          return false
        }
        const column = columns[cursor.main.columnIndex]
        const row = rows[cursor.main.rowIndex]
        if (column === undefined || row === undefined || !isCheckboxColumn(column)) {
          return false
        }
        return await createEmptyChildRecord(column, row)
      }

      switch (event.key) {
        case 'Enter': {
          if (cursor === undefined) {
            return
          }
          const column = columns[cursor.main.columnIndex]
          const row = rows[cursor.main.rowIndex]
          if (column === undefined || row === undefined) {
            return
          }
          if (wrappedEvent.commandKey) {
            if (column.nodeType === 'children') {
              await addChildRecord({
                rowIndex: cursor.main.rowIndex,
                innerRowIndex: cursor.main.innerRowIndex,
                node: column.node,
                additionalFieldValues: [],
              })
              moveToNextInnerRow()
            } else if (column.node.path.length <= 1) {
              const addedRowIndex = await addRootRecord({ rowIndex: cursor.main.rowIndex })
              if (addedRowIndex === undefined) {
                // 中断されたということのはず
                return
              }
              moveToRowIndex(addedRowIndex)
            }
          }
          // 単なるEnterのハンドリングは基本的にuseHandleKeyDownで行うが、空セルでEnterした際の挙動だけここで行う
          else if (column.nodeType === 'children' || column.nodeType === 'child') {
            await createEmptyChildRecord(column, row)
          }
          // eslint-disable-next-line unicorn/no-late-event-control
          event.preventDefault()
          break
        }
        case ' ': {
          // Space は基本 useHandleKeyDown が担当するが、空の checkbox/boolean 子セルだけは作成 + true セットをここで行う
          // (非 checkbox の空セル・非空セルは同期側の編集モード / トグルが担当するため、ここでは何もしない)
          if (await createEmptyCheckboxChildRecordAtCursor()) {
            // eslint-disable-next-line unicorn/no-late-event-control
            event.preventDefault()
          }
          break
        }
        case 's': {
          if (wrappedEvent.commandKey) {
            try {
              await recordsAndChangesMutation.onSave(setRecordSaveErrors)
            } catch (error) {
              if (error instanceof Error) {
                void message.error(error.message)
              } else {
                void message.error(t('エラーが発生しました'))
              }
            }
            // eslint-disable-next-line unicorn/no-late-event-control
            event.preventDefault()
          }
          break
        }
        case 'v': {
          if (wrappedEvent.commandKey) {
            await paste({ type: 'willReadClipboard' }).finally(() => {
              void message.info(t(`ペーストが完了しました`))
            })
            // eslint-disable-next-line unicorn/no-late-event-control
            event.preventDefault()
          }
          break
        }
      }
    },
    [
      addChildRecord,
      addRootRecord,
      columns,
      getCursorSnapshot,
      moveToNextInnerRow,
      moveToRowIndex,
      openViewRecordThreads,
      paste,
      query,
      recordsAndChangesMutation,
      rows,
      setRecordSaveErrors,
    ],
  )
}
